import { computed, ref } from 'vue';
import { JOINTS_OPTIONS } from '@/enums/global/GlobalOptions';

export const JOINT_OPTION_CATEGORY_KEYS = {
    COVER: 'cover-improvements',
    GUIDE: 'guide-improvements',
} as const;

export type JointOptionCategoryKey = (typeof JOINT_OPTION_CATEGORY_KEYS)[keyof typeof JOINT_OPTION_CATEGORY_KEYS];

export interface JointOptionCategory {
    key: JointOptionCategoryKey;
    label: string;
}

/**
 * Step 5 state: additional joint options.
 *
 * Owns the catalogue of optional joint extras shown in `AdditionalOptions.vue`
 * (left side) and previewed in `OptionsPreview.vue` (right side). Each entry
 * acts as a checkbox — the user can select all, some or none, and the step is
 * always skippable.
 *
 * This data is NOT part of the existing checklist `formData`; it is shared
 * configurator data and is written back to the configured checklist PID when
 * the joint is added.
 */
export interface JointOption {
    /** Small icon shown inside the selectable box (left side). */
    icon: string;
    /** Larger image shown in the preview pane when the option is selected. */
    previewImage: string;
    /** Human-readable label shown above the icon (passed through i18n). */
    label: string;
    /** Short helper text shown below the icon (passed through i18n). */
    text: string;
    /** Selection flag — toggled by clicking the option box. */
    isSelected: boolean;
    /** Dynamic tab/category membership for the option selector. */
    categoryKey: JointOptionCategoryKey;
    key: string;
}

export interface JointOptionTab extends JointOptionCategory {
    options: JointOption[];
}

const JOINT_OPTION_CATEGORIES: JointOptionCategory[] = [
    {
        key: JOINT_OPTION_CATEGORY_KEYS.COVER,
        label: 'Cover improvements',
    },
    {
        key: JOINT_OPTION_CATEGORY_KEYS.GUIDE,
        label: 'Guide improvements',
    },
];

const optionOneIcon: string = require('@/modules/ConfiguratorJoints/assets/option-1-icon.png');
const optionOnePreview: string = require('@/modules/ConfiguratorJoints/assets/option-1-preview.png');
const optionTwoIcon: string = require('@/modules/ConfiguratorJoints/assets/option-2-icon.png');
const optionTwoPreview: string = require('@/modules/ConfiguratorJoints/assets/option-2-preview.png');

function buildDefaultOptions(): JointOption[] {
    return [
        {
            icon: optionOneIcon,
            previewImage: optionOnePreview,
            label: 'Closing corner joint cover with additional support profiles',
            text: 'Covers the top of the corner joint with a closed cap.',
            isSelected: false,
            categoryKey: JOINT_OPTION_CATEGORY_KEYS.COVER,
            key: 'bottomCornerCover',
        },
        {
            icon: optionTwoIcon,
            previewImage: optionTwoPreview,
            label: 'Optional corner mask closure',
            text: 'Leaves the bottom of the corner joint open for drainage.',
            isSelected: false,
            categoryKey: JOINT_OPTION_CATEGORY_KEYS.GUIDE,
            key: 'closingPlate',
        },
    ];
}

export function useOptions() {
    const options = ref<JointOption[]>(buildDefaultOptions());

    const optionTabs = computed<JointOptionTab[]>(() =>
        JOINT_OPTION_CATEGORIES.map((category) => ({
            ...category,
            options: options.value.filter((option) => option.categoryKey === category.key),
        })),
    );

    const selectedOptions = computed<JointOption[]>(() => options.value.filter((option) => option.isSelected));

    function toggleOption(key: string) {
        const option = options.value.find((entry) => entry.key === key);
        if (!option) {
            return;
        }
        option.isSelected = !option.isSelected;
    }

    function resetOptions() {
        options.value.forEach((option) => {
            option.isSelected = false;
        });
    }

    return {
        // State
        options,
        optionTabs,
        selectedOptions,
        // Mutations
        toggleOption,
        resetOptions,
    };
}
